import { supabase } from '@/integrations/supabase/client';

/**
 * Fire-and-forget unauthorized access logger that is *durable enough*:
 *   - dedupes per (route + reason) per browser session so React re-render
 *     loops don't spam activity_log
 *   - retries once on transient failure (auth race, network blip)
 *   - surfaces failures to the console so silent drops are visible in QA
 *
 * Important: we still don't await this in render-path callers — React must
 * be free to redirect immediately. But the request is now monitored.
 */
export function logUnauthorizedAccess(route: string, reason: string): void {
  const key = `audit:unauth:${route}:${reason}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    /* sessionStorage may be unavailable (private mode); proceed anyway */
  }

  const attempt = async (tries = 0): Promise<void> => {
    const { error } = await supabase.rpc('log_unauthorized_access', {
      _route: route,
      _reason: reason,
    });
    if (error) {
      if (tries < 1) {
        await new Promise((r) => setTimeout(r, 600));
        return attempt(tries + 1);
      }
      // eslint-disable-next-line no-console
      console.error('[audit] log_unauthorized_access failed', { route, reason, error });
    }
  };
  void attempt();
}
